/**
 * Auth layer — staff SSO via the Worker broker.
 *
 * Sign-in is a full-page redirect to the Worker's OAuth broker
 * (`/api/auth/login` → token-issuer → Google Workspace). The session is the
 * Worker's httpOnly cookie; there is NO client-side session store. `getSession`
 * is authoritative from `/api/auth/me`. Role + `ROLE_LABELS` shape the UI only
 * — they are not the enforcement point (the eve `/api/eve/*` proxy is gated by
 * the staff session server-side).
 */

export type Role = 'admin' | 'internal' | 'carrier' | 'deployer' | 'general';

export interface AuthUser {
  id: string;
  name: string;
  first: string;
  email: string;
  org: string;
  initials: string;
  role: Role;
  // Usage / plan — placeholders until a usage service exists.
  plan: string;
  questionsUsed: number;
  questionLimit: number;
  resetLabel: string;
  /** 0 means "no monthly cap" (staff/internal accounts). */
  resetDays: number;
  /** True for demo guest sessions — drives guest-only UI notices. GUEST_AUTH (temporary). */
  guest?: boolean;
}

export interface Session {
  user: AuthUser;
}

/** Human label per role (single source of truth for role display). */
export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  internal: 'Internal',
  carrier: 'Carrier',
  deployer: 'Helium+ Deployer',
  general: 'General',
};

/** All roles in display order — the single source for role pickers. */
export const ALL_ROLES: Role[] = ['admin', 'internal', 'carrier', 'deployer', 'general'];

const ROLE_SET = new Set<string>(ALL_ROLES);
function isRole(value: unknown): value is Role {
  return typeof value === 'string' && ROLE_SET.has(value);
}

/**
 * Map the Worker's `/api/auth/me` payload to an AuthUser. Staff sign in via
 * SSO → internal-style account with no monthly cap; usage/plan are
 * placeholders until a usage service exists.
 */
// (`guest` threading is GUEST_AUTH (temporary) — remove with the feature.)
function userFromMe(me: { email: string; name?: string; role?: string; guest?: boolean }): AuthUser {
  const name = me.name?.trim() || me.email;
  const parts = name.split(/\s+/).filter(Boolean);
  const initials = (parts.map((w) => w[0]).slice(0, 2).join('') || me.email.slice(0, 2)).toUpperCase();
  return {
    id: me.email,
    name,
    first: parts[0] || name,
    email: me.email,
    org: me.guest ? 'Guest' : 'Nova Labs', // GUEST_AUTH (temporary)
    initials,
    role: isRole(me.role) ? me.role : 'general',
    plan: me.guest ? 'Demo' : 'Internal', // GUEST_AUTH (temporary)
    guest: me.guest, // GUEST_AUTH (temporary)
    questionsUsed: 0,
    questionLimit: 10000,
    resetLabel: '—',
    resetDays: 0,
  };
}

/**
 * Resolve the current session from the Worker's staff-SSO cookie via
 * `/api/auth/me`. Returns null when not signed in (or the broker is
 * unreachable), which sends the user to the login screen.
 */
export async function getSession(): Promise<Session | null> {
  try {
    const res = await fetch('/api/auth/me', { headers: { accept: 'application/json' } });
    if (res.ok) {
      // (`guest` threading is GUEST_AUTH (temporary) — remove with the feature.)
      const me = (await res.json()) as { authenticated?: boolean; email?: string; name?: string; role?: string; guest?: boolean };
      if (me.authenticated && me.email) {
        return { user: userFromMe({ email: me.email, name: me.name, role: me.role, guest: me.guest }) };
      }
    }
  } catch {
    /* broker unreachable — treat as signed out */
  }
  return null;
}

/** Begin staff SSO: hand off to the Worker's OAuth broker (full-page redirect).
 *  Carries the current path as `returnTo` so a deep link (e.g. /c/:id) reopens
 *  after login — the Worker validates it same-origin before honoring it. */
export function startStaffSignIn(): void {
  const returnTo = window.location.pathname + window.location.search;
  const q = returnTo && returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
  window.location.href = `/api/auth/login${q}`;
}

// GUEST_AUTH (temporary): password-only guest sign-in for client demos.
/**
 * POST the access code; returns null on success (the session cookie is set —
 * caller reloads) or a user-facing error message. Wrong code and throttled are
 * deliberately the same server response, so one message covers both.
 */
export async function guestSignIn(password: string): Promise<string | null> {
  try {
    const res = await fetch('/api/auth/guest', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (res.ok) return null;
    if (res.status === 404) return 'Guest access is not enabled.';
    return "That access code didn't work. Check it and try again.";
  } catch {
    return "Couldn't reach the sign-in service. Please try again.";
  }
}

export async function signOut(): Promise<void> {
  // The UI clears optimistically (see AuthProvider); the response also clears
  // the httpOnly cookie. Surface a failed revoke rather than swallowing it — on
  // a shared machine a silently-failed logout would leave the session live.
  try {
    const res = await fetch('/api/auth/logout', { method: 'POST', headers: { accept: 'application/json' } });
    if (!res.ok) console.warn('[auth] logout request failed:', res.status);
  } catch (err) {
    console.warn('[auth] logout request error:', err);
  }
}

/** Monthly questions remaining for a user. */
export function questionsLeft(user: AuthUser): number {
  return user.questionLimit - user.questionsUsed;
}
